import mapValues from 'lodash/mapValues'
import { createSelector } from 'reselect'

import {
  ABSORBANCE_READER_TYPE,
  FLEX_STACKER_MODULE_TYPE,
  getLabwareDisplayName,
  HEATERSHAKER_MODULE_TYPE,
  MAGNETIC_MODULE_TYPE,
  TEMPERATURE_MODULE_TYPE,
  VACUUM_MODULE_TYPE,
} from '@opentrons/shared-data'

import { getRobotType } from '/protocol-designer/file-data/selectors'

import { getInitialDeckSetup } from '../../step-forms/selectors'
import { getDeckSetupForActiveItem } from '../../top-selectors/labware-locations'
import { getLabwareNicknamesById } from '../labware/selectors'
import {
  getLabwareOnModule,
  getMagnetLabwareEngageHeight as getMagnetLabwareEngageHeightUtil,
  getModuleLabwareOptions,
  getModuleOnDeckByType,
  getModulesOnDeckByType,
} from './utils'

import type { DropdownOption } from '@opentrons/components'
import type { LabwareNamesByModuleId } from '../../steplist/types'
import type { Selector } from '../../types'

export const getLabwareNamesByModuleId: Selector<LabwareNamesByModuleId> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    (initialDeckSetup, nicknamesById) =>
      mapValues(initialDeckSetup.modules, (_, moduleId) => {
        const labware = getLabwareOnModule(initialDeckSetup, moduleId)
        return labware
          ? {
              nickname: nicknamesById[labware.id],
              displayName: getLabwareDisplayName(labware.def),
            }
          : null
      })
  )

/** Returns dropdown option for labware placed on magnetic module */
export const getMagneticLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    getRobotType,
    (initialDeckSetup, nicknamesById, robotType) => {
      return getModuleLabwareOptions(
        initialDeckSetup,
        nicknamesById,
        MAGNETIC_MODULE_TYPE,
        robotType
      )
    }
  )

/** Returns dropdown option for labware placed on temperature module */
export const getTemperatureLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    getRobotType,
    (initialDeckSetup, nicknamesById, robotType) => {
      const temperatureModuleOptions = getModuleLabwareOptions(
        initialDeckSetup,
        nicknamesById,
        TEMPERATURE_MODULE_TYPE,
        robotType
      )
      return temperatureModuleOptions
    }
  )

/** Returns dropdown option for labware placed on heater shaker module */
export const getHeaterShakerLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    getRobotType,
    (initialDeckSetup, nicknamesById, robotType) => {
      const heaterShakerModuleOptions = getModuleLabwareOptions(
        initialDeckSetup,
        nicknamesById,
        HEATERSHAKER_MODULE_TYPE,
        robotType
      )
      return heaterShakerModuleOptions
    }
  )

/** Returns dropdown option for labware placed on absorbance reader module */
export const getAbsorbanceReaderLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getDeckSetupForActiveItem,
    getLabwareNicknamesById,
    getRobotType,
    (deckSetup, nicknamesById, robotType) => {
      const absorbanceReaderModuleOptions = getModuleLabwareOptions(
        deckSetup,
        nicknamesById,
        ABSORBANCE_READER_TYPE,
        robotType
      )
      return absorbanceReaderModuleOptions
    }
  )

/** Returns dropdown option for labware placed on flex stacker module */
export const getFlexStackerLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    getRobotType,
    (initialDeckSetup, nicknamesById, robotType) => {
      const flexStackerModuleOptions = getModuleLabwareOptions(
        initialDeckSetup,
        nicknamesById,
        FLEX_STACKER_MODULE_TYPE,
        robotType
      )
      return flexStackerModuleOptions
    }
  )

export const getVacuumLabwareOptions: Selector<DropdownOption[]> =
  createSelector(
    getInitialDeckSetup,
    getLabwareNicknamesById,
    getRobotType,
    (initialDeckSetup, nicknamesById, robotType) => {
      const vacuumModuleOptions = getModuleLabwareOptions(
        initialDeckSetup,
        nicknamesById,
        VACUUM_MODULE_TYPE,
        robotType
      )
      return vacuumModuleOptions
    }
  )

/** Get single magnetic module (assumes no multiples) */
export const getSingleMagneticModuleId: Selector<string | null> =
  createSelector(
    getInitialDeckSetup,
    initialDeckSetup =>
      getModuleOnDeckByType(initialDeckSetup, MAGNETIC_MODULE_TYPE)?.id || null
  )

/** Get all temperature modules */
export const getTemperatureModuleIds: Selector<string[] | null> =
  createSelector(
    getInitialDeckSetup,
    initialDeckSetup =>
      getModulesOnDeckByType(initialDeckSetup, TEMPERATURE_MODULE_TYPE)?.map(
        module => module.id
      ) || null
  )
export const getMagnetLabwareEngageHeight: Selector<number | null> =
  createSelector(
    getInitialDeckSetup,
    getSingleMagneticModuleId,
    (initialDeckSetup, magnetModuleId) =>
      getMagnetLabwareEngageHeightUtil(initialDeckSetup, magnetModuleId)
  )
